import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import CSSTransition from 'react-transition-group/CSSTransition';
import TransitionGroup from 'react-transition-group/TransitionGroup';

import FormError from '@gef-ui/error-report-material/components/FormError';
import InlineError from '@gef-ui/error-report-material/components/InlineError';
import { getLabelMessage } from '@gef-ui/localization/messages';

import { EMPTY_VALUES } from 'constants/general';
import fetch from 'features/common/fetch';
import { toBeLogDateTime } from 'utils/apiDateTime';
import { equalOrThreeDashes } from 'utils/equalOrThreeDashes';
import { joinString } from 'utils/string';

export const ComponentLoader = ({
	className = EMPTY_VALUES.STRING,
	isLoading = false,
	loaded = false,
	error = false,
	large = false,
	metaData = null,
	renderOnlyIfLoaded = false,
	fetchStorePath,
	forceInlineError = false,
	CustomErrorComponent,
	onReload,
	onClose,
	ignoreErrors,
	children,
}) => {
	const fetchState = useSelector(fetch.selectors.getFetchState(fetchStorePath || ''));
	const errorMetaData = useSelector(fetch.selectors.getMetaData(fetchStorePath || ''));

	const isLoadingValue = isLoading || fetchState.isFetching;
	const loadedValue = loaded || fetchState.loaded;
	const hasError = (error || fetchState.error) && !ignoreErrors;
	const errorValue = metaData || errorMetaData;

	const getClassName = () => {
		return isLoadingValue && !hasError && !loadedValue ? getLoaderClassname() : getPropsClassName();
	};

	const getPropsClassName = () => {
		return className || '';
	};

	const getLoaderClassname = () => {
		const loaderClassName = 'Preloader';
		return large
			? `${getPropsClassName()} ${loaderClassName} ${loaderClassName}--sizeLarge`
			: `${loaderClassName} ${getPropsClassName()}`;
	};

	const renderContent = useMemo(() => {
		if (renderOnlyIfLoaded && !loadedValue) {
			return null;
		}

		if (!hasError) {
			return children;
		}

		return null;
	}, [renderOnlyIfLoaded, loadedValue, hasError, children]);

	const renderError = useMemo(() => {
		if (hasError) {
			const heading = errorValue?.meta?.errorReport?.heading || getLabelMessage('error:unexpected');

			const shortDescription =
				errorValue?.meta?.errorReport?.shortDescription ||
				errorValue?.parsedContent?.path ||
				errorValue?.parsedContent?.type ||
				heading;

			const longDescription =
				errorValue?.meta?.errorReport?.longDescription ||
				errorValue?.parsedContent?.stacktrace ||
				shortDescription;

			const status = `Status: ${equalOrThreeDashes(errorValue?.status)}`;
			const method = `Method: ${equalOrThreeDashes(errorValue?.requestParams?.method)}`;
			const url = `Url: ${equalOrThreeDashes(errorValue?.requestParams?.url)}`;
			const payload = `Payload: ${equalOrThreeDashes(errorValue?.requestParams?.body)}`;
			const response = `Response: ${equalOrThreeDashes(longDescription)}`;
			const traceId = `TraceId: ${equalOrThreeDashes(errorValue?.meta?.errorReport?.traceId)}`;

			const fullLongDescription = joinString('; ', [status, method, url, payload, response, traceId]);

			if (CustomErrorComponent) {
				return CustomErrorComponent;
			}

			if (forceInlineError) {
				return (
					<InlineError
						className="Error--inline"
						message={heading}
						onRefresh={onReload}
						onClose={onClose ? onClose : undefined}
					/>
				);
			}

			return (
				<FormError
					error={{
						heading,
						shortDescription,
						longDescription: fullLongDescription,
						timestamp: toBeLogDateTime(
							errorValue?.meta?.errorReport?.timestamp || errorValue?.parsedContent?.timestamp,
						),
						traceId: errorValue?.meta?.errorReport?.traceId,
						screen: window.screenId,
					}}
					withSendOption
					onRefresh={onReload}
					onClose={onClose ? onClose : false}
				/>
			);
		}
	}, [
		hasError,
		errorValue?.status,
		errorValue?.meta?.errorReport?.heading,
		errorValue?.meta?.errorReport?.shortDescription,
		errorValue?.meta?.errorReport?.longDescription,
		errorValue?.meta?.errorReport?.timestamp,
		errorValue?.meta?.errorReport?.traceId,
		errorValue?.parsedContent?.path,
		errorValue?.parsedContent?.type,
		errorValue?.parsedContent?.stacktrace,
		errorValue?.parsedContent?.timestamp,
		errorValue?.requestParams?.body,
		errorValue?.requestParams?.method,
		errorValue?.requestParams?.url,
		CustomErrorComponent,
		forceInlineError,
		onReload,
		onClose,
	]);

	return (
		<div className={getClassName()}>
			<TransitionGroup>
				<CSSTransition className="transition" timeout={{ enter: 500, exit: 300 }}>
					<div>{hasError && renderError}</div>
				</CSSTransition>
				<CSSTransition className="transition" timeout={{ enter: 500, exit: 300 }}>
					<div>{renderContent}</div>
				</CSSTransition>
			</TransitionGroup>
		</div>
	);
};

ComponentLoader.propTypes = {
	CustomErrorComponent: PropTypes.node,
	children: PropTypes.any,
	className: PropTypes.string,
	error: PropTypes.bool,
	fetchStorePath: PropTypes.string,
	forceInlineError: PropTypes.bool,
	ignoreErrors: PropTypes.bool,
	isLoading: PropTypes.bool,
	large: PropTypes.bool,
	loaded: PropTypes.bool,
	metaData: PropTypes.object,
	onClose: PropTypes.func,
	onReload: PropTypes.func,
	renderOnlyIfLoaded: PropTypes.bool,
};

export default ComponentLoader;
